// ═══════════════════════════════════════════════════════════════
// Shelter Reunification — fuzzy matching helpers
// Used by both the authenticated Reunification page and the
// public reunification request form.
// ═══════════════════════════════════════════════════════════════

export function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const d: number[][] = Array.from({ length: m + 1 }, (_, i) => {
    const row = Array(n + 1).fill(0);
    row[0] = i;
    return row;
  });
  for (let j = 1; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      d[i][j] = Math.min(d[i-1][j]+1, d[i][j-1]+1, d[i-1][j-1]+(a[i-1]===b[j-1]?0:1));
  return d[m][n];
}

export function scoreNameMatch(query: string, first: string, last: string): { score: number; confidence: number } | null {
  const q = query.toLowerCase().trim();
  if (!q) return null;
  const qParts = q.split(/\s+/);
  const full = `${first} ${last}`.toLowerCase();
  const f = (first || '').toLowerCase();
  const l = (last || '').toLowerCase();
  if (full.includes(q)) return { score: 0, confidence: 100 };
  let totalDist = 0;
  for (const part of qParts) {
    const dFirst = levenshtein(part, f.slice(0, part.length + 2));
    const dLast = levenshtein(part, l.slice(0, part.length + 2));
    totalDist += Math.min(dFirst, dLast);
  }
  const threshold = qParts.length * 2;
  if (totalDist > threshold) return null;
  const confidence = Math.max(40, Math.round(100 - (totalDist / (threshold + 1)) * 60));
  return { score: totalDist, confidence };
}

export function ageFromDob(dob: string): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
}

export type SurvivorLike = { firstName?: string; lastName?: string; dateOfBirth?: string; phone?: string };
export type QueryRec = { name: string; dob?: string; phone?: string; age?: string };

export function scoreFullMatch(q: QueryRec, s: SurvivorLike): { confidence: number; reasons: string[] } | null {
  const nameRes = scoreNameMatch(q.name || '', s.firstName || '', s.lastName || '');
  let confidence = nameRes ? nameRes.confidence : 0;
  const reasons: string[] = [];
  if (nameRes) reasons.push('name');

  if (q.dob && s.dateOfBirth) {
    const a = q.dob.slice(0, 10);
    const b = s.dateOfBirth.slice(0, 10);
    if (a && b && a === b) {
      confidence = Math.min(100, Math.max(confidence, 80) + 20);
      reasons.push('DOB');
    } else if (a.slice(0, 4) === b.slice(0, 4) && a.length >= 4) {
      confidence += 5;
      reasons.push('birth year');
    }
  }

  if (q.phone && s.phone) {
    const a = q.phone.replace(/\D/g, '').slice(-7);
    const b = s.phone.replace(/\D/g, '').slice(-7);
    if (a.length >= 7 && a === b) {
      confidence = Math.min(100, Math.max(confidence, 70) + 25);
      reasons.push('phone');
    }
  }

  if (q.age && s.dateOfBirth) {
    const survAge = ageFromDob(s.dateOfBirth);
    const reqAge = parseInt(q.age, 10);
    if (survAge !== null && !isNaN(reqAge) && Math.abs(survAge - reqAge) <= 2) {
      confidence += 10;
      reasons.push('age');
    }
  }

  if (confidence === 0) return null;
  return { confidence: Math.min(100, confidence), reasons };
}

export function confColor(c: number): string {
  return c >= 90 ? 'green' : c >= 75 ? 'blue' : c >= 60 ? 'yellow' : 'gray';
}
